import * as React from 'react';
import { SearchField } from './SearchField';
import { SearchResult, SearchCategory } from '../../Model/core';
import { debounceMethod } from '../CourseMatrix/debonce';
import { search, itemLength } from './api';
import { Result } from './Result';
import { autobind } from '../CourseMatrix/autobind';

enum KeyCode {
    Esc = 27
}

interface CategoryPageResult {
    [category: number]: {
        fetching: boolean;
        noMore: boolean;
        page: number;
    };
}

interface Props {
}

interface State {
    value: string;
    result: SearchResult | null;
    showResult: boolean;
    catResult: CategoryPageResult;
}

export class Search extends React.Component<Props, State> {

    private resultElm: HTMLDivElement | null = null;
    private searchInputElm: HTMLDivElement | null = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            value: '',
            result: null,
            showResult: false,
            catResult: {}
        };
    }

    @autobind
    handleKeyInputs(e: KeyboardEvent) {
        if (e.keyCode === KeyCode.Esc) {
            this.setState({ showResult: false });
        }
    }

    @autobind
    handleMouseClick(e: MouseEvent) {
        let element = document.elementFromPoint(e.clientX, e.clientY);

        while (element && element !== document.body) {
            if (this.resultElm === element || this.searchInputElm === element) {
                return;
            }

            element = element.parentElement;
        }

        this.setState({ showResult: false });
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyInputs);
        window.addEventListener('mousedown', this.handleMouseClick);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyInputs);
        window.removeEventListener('mousedown', this.handleMouseClick);
    }

    @debounceMethod(200, { autobind: true })
    async search(term: string) {
        if (!term || !term.trim().length) {
            this.setState({ result: null, showResult: false });
            return;
        }

        const result = await search(term);
        this.setState({ result, showResult: true, catResult: {} });
    }

    @autobind
    async more(cat: SearchCategory) {
        const res = this.state.result?.Groups.find(g => g.Category === cat);

        if (!res) {
            return;
        }

        const prevRes = this.state.catResult?.[cat];

        if (prevRes?.noMore || prevRes?.fetching) {
            return;
        }

        this.setState({
            catResult: {
                ...this.state.catResult,
                [cat]: {
                    ...this.state.catResult[cat],
                    fetching: true
                }
            }
        });

        const page = (prevRes?.page ?? 0) + 1;

        const searchRes = await search(this.state.value, page, cat);
        const items = searchRes.Groups.find(g => g.Category === cat)?.Result ?? [];

        res.Result.push(...items);

        this.setState({
            catResult: {
                ...this.state.catResult,
                [cat]: {
                    page,
                    fetching: false,
                    noMore: items.length < itemLength
                }
            }
        });
    }

    render() {
        const show = this.state.showResult && !!this.state.result;

        return (
            <div className='search'>
                <SearchField
                    element={elm => this.searchInputElm = elm}
                    value={this.state.value}
                    focus={() => this.setState({ showResult: true })}
                    change={value => this.setState({ value }, () => this.search(value))} />

                <Result
                    element={elm => this.resultElm = elm}
                    query={this.state.value}
                    visible={show}
                    result={this.state.result}
                    more={this.more} />
            </div>
        );
    }
}
